/**
 * Shared plumbing for the Driver Manager charts that join ERM events to
 * scorecard drivers.
 *
 * Two things live here, both used by more than one surface:
 *
 * 1. The driver JOIN. The two datasets do not share an identifier -- the
 *    scorecard keys on a telematics id, while an ERM event carries `driver_id`
 *    as FREE TEXT ("Driver ID or name" on the staff form). matchDrivers() is a
 *    best-effort match in four passes and reports what it could not place,
 *    because coverage is a first-class result rather than an implementation
 *    detail.
 *
 * 2. The quadrant-chart FURNITURE -- guide lines, corner labels, the type
 *    scale and the padding a bubble needs so it is not sliced by an axis.
 *
 * Extracted from the old risk_matrix.js when the behaviour-vs-outcomes page
 * was retired (2026-08-25) and Exposure took the Risk Matrix name. The
 * outcome-score model that used to live alongside this went with that page.
 */

/**
 * Fold a driver string to a comparison key: case, punctuation and repeated
 * whitespace all vary between what a dispatcher types into the ERM form and
 * what the telematics provider has on file ("O'Brien" / "obrien",
 * "J. Kowalczyk" / "J Kowalczyk").
 */
export function normalizeDriverKey(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    // Drop bracketed or parenthesised trailing tokens BEFORE folding
    // punctuation. Operators tag events with the payroll/employee number
    // beside the name -- Anderson's whole ERM history is
    // "Lastname, Firstname [1161]" -- and that number is not the telematics
    // id the scorecard keys on, so it is noise for matching, not signal.
    // Without this, every such string misses: measured 0 of 881 events
    // matching on Anderson prod (2026-08-24).
    .replace(/[[(][^\])]*[\])]/g, ' ')
    .replace(/[.,'’\-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Comparison keys a roster name should answer to. Rosters are inconsistent
 * about "Last, First" vs "First Last", and the two forms are common enough in
 * the same tenant that matching only the stored order loses real drivers.
 */
export function nameVariants(name) {
  const base = normalizeDriverKey(name)
  if (!base) return []
  const out = [base]
  const parts = base.split(' ').filter(Boolean)
  if (parts.length === 2) {
    // "kowalczyk jan" <-> "jan kowalczyk"; a comma in the source has already
    // been folded to a space by normalizeDriverKey, so both orders are just
    // two-token permutations by this point.
    out.push(`${parts[1]} ${parts[0]}`)
  }
  return out
}

/**
 * Keys of the form "surname|f" for both name orders. Returns [] for anything
 * that is not two tokens, so placeholders ("none", "Answering Service") and
 * longer names never reach the fuzzy pass.
 */
export function surnameInitialKeys(name) {
  const out = []
  nameVariants(name).forEach(v => {
    const parts = v.split(' ').filter(Boolean)
    if (parts.length !== 2) return
    const k = `${parts[0]}|${parts[1][0]}`
    if (!out.includes(k)) out.push(k)
  })
  return out
}

function blankAggregate() {
  return { events: 0, issues: 0, at_fault: 0, kudos: 0, delayed: 0, keys: [] }
}

function addAggregate(target, row) {
  target.events += Number(row.events) || 0
  target.issues += Number(row.issues) || 0
  target.at_fault += Number(row.at_fault) || 0
  target.kudos += Number(row.kudos) || 0
  target.delayed += Number(row.delayed) || 0
  if (row.driver_key) target.keys.push(row.driver_key)
  return target
}

/**
 * Match raw ERM driver strings onto scorecard rows.
 *
 * Three passes, most to least certain: exact driver_id, then normalized name,
 * then the swapped-order name. Several ERM strings can land on one driver (an
 * id from one submitter, a name from another) -- their counts are summed.
 *
 * @returns {{ byDriverId: Object, unmatched: Array, matchedDriverCount: number }}
 */
export function matchDrivers(scorecardRows, ermRows) {
  const byId = new Map()
  const byName = new Map()
  // surname + first initial -> roster rows. Short forms are pervasive in
  // hand-typed driver fields ("Lininger, Walt" for Walter, "Burgess, Frank"
  // for Frankie, "Eaton, Don" for Donald), and a nickname table would be a
  // guess list nobody maintains. This is the same evidence without the
  // guessing -- and it is only consulted when EXACTLY ONE roster driver has
  // that surname+initial, so it can never silently pick between two people.
  const bySurnameInitial = new Map()

  ;(scorecardRows || []).forEach(row => {
    const id = row && row.driver_id != null ? String(row.driver_id).trim() : ''
    if (id) {
      byId.set(id, row)
      // an id is also matchable in its folded form: submitters paste ids with
      // stray case or spacing too.
      const foldedId = normalizeDriverKey(id)
      if (foldedId && !byName.has(foldedId)) byName.set(foldedId, row)
    }
    nameVariants(row && row.driver_name).forEach(variant => {
      // first roster row wins a contested name: with two same-named drivers
      // there is no evidence in the event to choose between them, and
      // silently splitting their events across both would be worse.
      if (!byName.has(variant)) byName.set(variant, row)
    })
    surnameInitialKeys(row && row.driver_name).forEach(k => {
      const seen = bySurnameInitial.get(k)
      if (seen === undefined) bySurnameInitial.set(k, row)
      else if (seen !== row) bySurnameInitial.set(k, null)   // ambiguous: never match
    })
  })

  const byDriverId = {}
  const unmatched = []

  ;(ermRows || []).forEach(row => {
    const key = row && row.driver_key != null ? String(row.driver_key).trim() : ''
    if (!key) return

    const folded = normalizeDriverKey(key)
    let hit = byId.get(key) || byName.get(folded)
    if (!hit) {
      // last resort, and only on an unambiguous surname+initial
      for (const k of surnameInitialKeys(folded)) {
        const cand = bySurnameInitial.get(k)
        if (cand) { hit = cand; break }
      }
    }
    if (!hit) {
      unmatched.push(row)
      return
    }
    const id = String(hit.driver_id)
    if (!byDriverId[id]) byDriverId[id] = blankAggregate()
    addAggregate(byDriverId[id], row)
  })

  return {
    byDriverId,
    unmatched,
    matchedDriverCount: Object.keys(byDriverId).length,
  }
}


// chart.js wants a fill distinct from the border; the quadrant hues are plain
// 6-digit hex so this stays a substring op rather than a colour library.
export function hexAlpha(hex, alpha) {
  const h = String(hex).replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Draws the quadrant split lines and their corner labels underneath the
 * bubbles. chartjs-plugin-annotation is not a dependency, and this needs two
 * straight lines and four labels -- not a plugin's worth of surface.
 *
 * Takes the font family through options rather than reading Chart.defaults, so
 * this module stays free of a chart.js import and unit-testable as plain JS.
 * Options: { xMid, yMid, lineColor, fontFamily, labels{}, labelColors{} }.
 */
export const quadrantGuidesPlugin = {
  id: 'rmQuadrantGuides',
  beforeDatasetsDraw(chart, args, opts) {
    const { ctx, chartArea, scales } = chart
    if (!chartArea || !scales.x || !scales.y || !opts || !opts.labels) return
    const xMid = scales.x.getPixelForValue(opts.xMid)
    const yMid = scales.y.getPixelForValue(opts.yMid)
    ctx.save()
    ctx.strokeStyle = opts.lineColor
    ctx.lineWidth = opts.lineWidth || MATRIX_GUIDE_LINE_WIDTH
    ctx.setLineDash(opts.lineDash || MATRIX_GUIDE_LINE_DASH)
    ctx.beginPath()
    ctx.moveTo(xMid, chartArea.top)
    ctx.lineTo(xMid, chartArea.bottom)
    ctx.moveTo(chartArea.left, yMid)
    ctx.lineTo(chartArea.right, yMid)
    ctx.stroke()
    ctx.setLineDash([])

    const fontSize = opts.labelFontSize || 10
    ctx.font = `700 ${fontSize}px ${opts.fontFamily || 'sans-serif'}`
    const pad = opts.labelPadding == null ? MATRIX_CORNER_LABEL_PADDING : opts.labelPadding
    const labels = [
      { text: opts.labels.topLeft, x: chartArea.left + pad, y: chartArea.top + pad, align: 'left', color: opts.labelColors.topLeft },
      { text: opts.labels.topRight, x: chartArea.right - pad, y: chartArea.top + pad, align: 'right', color: opts.labelColors.topRight },
      { text: opts.labels.bottomLeft, x: chartArea.left + pad, y: chartArea.bottom - pad - fontSize, align: 'left', color: opts.labelColors.bottomLeft },
      { text: opts.labels.bottomRight, x: chartArea.right - pad, y: chartArea.bottom - pad - fontSize, align: 'right', color: opts.labelColors.bottomRight },
    ]
    ctx.textBaseline = 'top'
    labels.forEach(l => {
      ctx.textAlign = l.align
      ctx.fillStyle = l.color
      ctx.globalAlpha = 0.75
      ctx.fillText(l.text, l.x, l.y)
    })
    ctx.restore()
  },
}

// Type scale for the quadrant charts' furniture, shared so a page and its
// dashboard widget cannot drift apart. Bigger and bolder than chart.js's
// defaults on purpose: the legend doubles as the quadrant key and the axis
// titles carry the whole "behaviour vs outcomes" idea, so both are content
// here rather than decoration.
export const MATRIX_LEGEND_FONT = { size: 13, weight: '700' }
export const MATRIX_AXIS_TITLE_FONT = { size: 13, weight: '700' }
// breathing room: between legend keys, between an axis title and its ticks,
// and between the plot edge and the four corner labels. chart.js's defaults
// (10 / 4 / none) crowd all three once the type is set this large.
export const MATRIX_LEGEND_PADDING = 18
// gap between a legend key's point-style circle and its text. chart.js
// defaults this to 1, which reads as the dot touching the word.
export const MATRIX_LEGEND_BOX_PADDING = 10
export const MATRIX_AXIS_TITLE_PADDING = 12
export const MATRIX_CORNER_LABEL_PADDING = 18
// keeps the corner labels off the plot's own border
export const MATRIX_LAYOUT_PADDING = { top: 10, right: 14, bottom: 6, left: 6 }

/**
 * Layout padding wide enough that a bubble sitting ON an axis bound still
 * draws its whole circle. Chart.js clips to the plot area, so a point at
 * exactly the min or max loses half its dot -- which happens constantly here:
 * the outcome score is clamped to 100, and Exposure pins its outliers at the
 * axis bound on purpose. Pair with `clip: false` on the dataset, which lets
 * the overflow render into this padding instead of being cut.
 */
export function bubbleLayoutPadding(maxRadius) {
  const need = Math.ceil((Number(maxRadius) || 0) + 3)
  return {
    top: Math.max(MATRIX_LAYOUT_PADDING.top, need),
    right: Math.max(MATRIX_LAYOUT_PADDING.right, need),
    bottom: Math.max(MATRIX_LAYOUT_PADDING.bottom, need),
    left: Math.max(MATRIX_LAYOUT_PADDING.left, need),
  }
}

// The quadrant split lines are the chart's structure, not its gridlines --
// which quadrant a bubble is in is the entire reading, so these have to be
// clearly stronger than the background grid rather than a hair thinner.
export const MATRIX_GUIDE_LINE_WIDTH = 2
export const MATRIX_GUIDE_LINE_DASH = [7, 5]
export function matrixGuideLineColor(midnight) {
  return midnight ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)'
}

